import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MoodPicker } from "./MoodPicker";
import { Bell, BellOff } from "lucide-react";
import { chime } from "@/lib/chime";
import { markActive } from "@/lib/seatTime";

const PHASES = [
  { label: "Breathe in", secs: 4, scale: 1.35 },
  { label: "Hold gently", secs: 4, scale: 1.35 },
  { label: "Breathe out", secs: 6, scale: 0.85 },
];
const CYCLES = 3;

export const CenteringStep = ({ topic, data, setData, onDone }) => {
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState(0);
  const [cycle, setCycle] = useState(0);
  const [sound, setSound] = useState(true);
  const startedAt = useRef(null);

  useEffect(() => {
    if (!running) return;
    markActive();
    if (sound) chime([528, 639, 432][phase]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, phase, cycle]);

  useEffect(() => {
    if (!running) return;
    const t = setTimeout(() => {
      if (phase < PHASES.length - 1) return setPhase(phase + 1);
      if (cycle + 1 >= CYCLES) { setRunning(false); setCycle(CYCLES); return; }
      setPhase(0); setCycle(cycle + 1);
    }, PHASES[phase].secs * 1000);
    return () => clearTimeout(t);
  }, [running, phase, cycle]);

  const begin = () => { startedAt.current = Date.now(); setPhase(0); setCycle(0); setRunning(true); };
  const finish = () => {
    const mins = startedAt.current ? Math.max(0.5, (Date.now() - startedAt.current) / 60000) : 0.5;
    setData({ ...data, mindful_minutes: Math.round(mins * 10) / 10 });
    onDone();
  };
  const complete = cycle >= CYCLES;
  const p = PHASES[phase];

  return (
    <div className="grid lg:grid-cols-2 gap-12 items-center" data-testid="centering-step">
      <div className="flex flex-col items-center">
        <div className="relative w-72 h-72 grid place-items-center">
          <motion.div className="absolute inset-6 rounded-full bg-[#E8B4B8]/40" animate={{ scale: running ? p.scale : 1 }} transition={{ duration: running ? p.secs : 0.6, ease: "easeInOut" }} />
          <motion.div className="absolute inset-16 rounded-full bg-[#6B9080]/50" animate={{ scale: running ? p.scale : 1 }} transition={{ duration: running ? p.secs : 0.6, ease: "easeInOut", delay: 0.1 }} />
          <div className="relative text-center" data-testid="breathing-phase-label">
            <p className="font-display text-2xl text-[#2C4A3E]">{complete ? "Well done" : running ? p.label : "Ready?"}</p>
            <p className="text-xs text-stone-500 mt-1">{complete ? "You are centered" : running ? `Breath ${cycle + 1} of ${CYCLES}` : "3 calm breaths"}</p>
          </div>
        </div>
        {!running && !complete && (
          <button onClick={begin} data-testid="start-breathing-btn" className="mt-6 px-6 py-3 rounded-full bg-[#2C4A3E] text-white hover:bg-[#1E352B] transition-colors">Start breathing</button>
        )}
        <button onClick={() => setSound(!sound)} data-testid="chime-toggle-btn" data-state={sound ? "on" : "off"}
          className="mt-3 inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-[#2C4A3E] transition-colors">
          {sound ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />} Gentle chime {sound ? "on" : "off"}
        </button>
      </div>
      <div className="space-y-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-stone-500">How do you feel right now?</p>
          <div className="mt-3"><MoodPicker value={data.mood_before} onChange={(v) => setData({ ...data, mood_before: v })} testId="mood-before" /></div>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-stone-500">{topic ? `Set an intention for "${topic}"` : "Set an intention"}</p>
          <input value={data.intention} onChange={(e) => setData({ ...data, intention: e.target.value })} data-testid="intention-input"
            placeholder="e.g. I will stay curious even when it's hard"
            className="mt-3 w-full px-5 py-3 rounded-2xl bg-white/70 border border-[#E6DEC3] focus:outline-none focus:border-[#2C4A3E]" />
        </div>
        <div className="flex gap-3">
          <button onClick={finish} data-testid="centering-continue-btn"
            className="px-6 py-3 rounded-full bg-[#C87D53] text-white hover:bg-[#B36C44] transition-colors">
            {complete ? "Continue to lesson" : "Skip & continue"}
          </button>
        </div>
      </div>
    </div>
  );
};
